import { useRef, useState, useEffect } from 'react';
import { Stage, Layer, Rect, Circle, Line, Text, Transformer } from 'react-konva';
import Konva from 'konva';
import type { FloorPlanShape, FloorPlanDrawingData } from '@/types';

interface FloorPlanCanvasProps {
  drawingData: FloorPlanDrawingData;
  onChange: (data: FloorPlanDrawingData) => void;
  selectedTool: 'select' | 'rect' | 'circle' | 'line' | 'text' | 'delete';
  width?: number;
  height?: number;
}

export const FloorPlanCanvas = ({
  drawingData,
  onChange,
  selectedTool,
  width = 800,
  height = 600,
}: FloorPlanCanvasProps) => {
  const stageRef = useRef<Konva.Stage>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [newShape, setNewShape] = useState<FloorPlanShape | null>(null);

  // Update transformer when selection changes
  useEffect(() => {
    if (!transformerRef.current || !stageRef.current) return;

    if (selectedId) {
      const selectedNode = stageRef.current.findOne(`#${selectedId}`);
      if (selectedNode) {
        transformerRef.current.nodes([selectedNode]);
        transformerRef.current.getLayer()?.batchDraw();
      }
    } else {
      transformerRef.current.nodes([]);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [selectedId]);

  const generateId = () => `shape-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const handleStageMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
    const clickedOnEmpty = e.target === e.target.getStage();
    
    if (clickedOnEmpty) {
      setSelectedId(null);
    }

    if (selectedTool === 'select' || selectedTool === 'delete') {
      return;
    }

    const stage = stageRef.current;
    if (!stage) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    // Scale pointer position back to original coordinates
    const scaleX = drawingData.width / width;
    const scaleY = drawingData.height / height;
    const scaledPos = {
      x: pos.x * scaleX,
      y: pos.y * scaleY,
    };

    setIsDrawing(true);

    const id = generateId();

    if (selectedTool === 'rect') {
      setNewShape({
        id,
        type: 'rect',
        x: scaledPos.x,
        y: scaledPos.y,
        width: 0,
        height: 0,
        fill: '#e2e8f0',
        stroke: '#64748b',
      });
    } else if (selectedTool === 'circle') {
      setNewShape({
        id,
        type: 'circle',
        x: scaledPos.x,
        y: scaledPos.y,
        radius: 0,
        fill: '#e2e8f0',
        stroke: '#64748b',
      });
    } else if (selectedTool === 'line') {
      setNewShape({
        id,
        type: 'line',
        x: 0,
        y: 0,
        points: [scaledPos.x, scaledPos.y, scaledPos.x, scaledPos.y],
        stroke: '#64748b',
      });
    } else if (selectedTool === 'text') {
      const text = prompt('Szöveg megadása:');
      if (text) {
        const newTextShape: FloorPlanShape = {
          id,
          type: 'text',
          x: scaledPos.x,
          y: scaledPos.y,
          text,
          fill: '#1e293b',
        };
        onChange({
          ...drawingData,
          shapes: [...drawingData.shapes, newTextShape],
        });
      }
      setIsDrawing(false);
    }
  };

  const handleStageMouseMove = (_e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!isDrawing || !newShape) return;

    const stage = stageRef.current;
    if (!stage) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    // Scale pointer position back to original coordinates
    const scaleX = drawingData.width / width;
    const scaleY = drawingData.height / height;
    const scaledPos = {
      x: pos.x * scaleX,
      y: pos.y * scaleY,
    };

    if (newShape.type === 'rect') {
      setNewShape({
        ...newShape,
        width: scaledPos.x - newShape.x,
        height: scaledPos.y - newShape.y,
      });
    } else if (newShape.type === 'circle') {
      const dx = scaledPos.x - newShape.x;
      const dy = scaledPos.y - newShape.y;
      setNewShape({
        ...newShape,
        radius: Math.sqrt(dx * dx + dy * dy),
      });
    } else if (newShape.type === 'line' && newShape.points) {
      setNewShape({
        ...newShape,
        points: [newShape.points[0], newShape.points[1], scaledPos.x, scaledPos.y],
      });
    }
  };

  const handleStageMouseUp = () => {
    if (!isDrawing || !newShape) return;

    setIsDrawing(false);

    // Only add shape if it has some size
    const isValidShape =
      (newShape.type === 'rect' && newShape.width && Math.abs(newShape.width) > 5) ||
      (newShape.type === 'circle' && newShape.radius && newShape.radius > 5) ||
      (newShape.type === 'line' && newShape.points);

    if (isValidShape) {
      // Normalize negative dimensions for rect
      let finalShape = { ...newShape };
      if (finalShape.type === 'rect' && finalShape.width && finalShape.height) {
        if (finalShape.width < 0) {
          finalShape.x += finalShape.width;
          finalShape.width = Math.abs(finalShape.width);
        }
        if (finalShape.height < 0) {
          finalShape.y += finalShape.height;
          finalShape.height = Math.abs(finalShape.height);
        }
      }

      onChange({
        ...drawingData,
        shapes: [...drawingData.shapes, finalShape],
      });
    }

    setNewShape(null);
  };

  const handleShapeClick = (shapeId: string) => {
    if (selectedTool === 'delete') {
      onChange({
        ...drawingData,
        shapes: drawingData.shapes.filter((s) => s.id !== shapeId),
      });
    } else if (selectedTool === 'select') {
      setSelectedId(shapeId);
    }
  };

  const handleShapeDragEnd = (shapeId: string, e: Konva.KonvaEventObject<DragEvent>) => {
    const node = e.target;
    onChange({
      ...drawingData,
      shapes: drawingData.shapes.map((shape) =>
        shape.id === shapeId
          ? { ...shape, x: node.x(), y: node.y() }
          : shape
      ),
    });
  };

  const handleTransformEnd = (shapeId: string, e: Konva.KonvaEventObject<Event>) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    // Reset scale
    node.scaleX(1);
    node.scaleY(1);

    onChange({
      ...drawingData,
      shapes: drawingData.shapes.map((shape) => {
        if (shape.id !== shapeId) return shape;

        if (shape.type === 'rect') {
          return {
            ...shape,
            x: node.x(),
            y: node.y(),
            width: Math.max(5, (shape.width || 0) * scaleX),
            height: Math.max(5, (shape.height || 0) * scaleY),
          };
        } else if (shape.type === 'circle') {
          return {
            ...shape,
            x: node.x(),
            y: node.y(),
            radius: Math.max(5, (shape.radius || 0) * Math.max(scaleX, scaleY)),
          };
        }
        return { ...shape, x: node.x(), y: node.y() };
      }),
    });
  };

  const renderShape = (shape: FloorPlanShape) => {
    const commonProps = {
      id: shape.id,
      draggable: selectedTool === 'select',
      onClick: () => handleShapeClick(shape.id),
      onTap: () => handleShapeClick(shape.id),
      onDragEnd: (e: Konva.KonvaEventObject<DragEvent>) => handleShapeDragEnd(shape.id, e),
      onTransformEnd: (e: Konva.KonvaEventObject<Event>) => handleTransformEnd(shape.id, e),
    };

    switch (shape.type) {
      case 'rect':
        return (
          <Rect
            key={shape.id}
            {...commonProps}
            x={shape.x}
            y={shape.y}
            width={shape.width}
            height={shape.height}
            fill={shape.fill}
            stroke={shape.stroke}
            strokeWidth={2}
          />
        );
      case 'circle':
        return (
          <Circle
            key={shape.id}
            {...commonProps}
            x={shape.x}
            y={shape.y}
            radius={shape.radius}
            fill={shape.fill}
            stroke={shape.stroke}
            strokeWidth={2}
          />
        );
      case 'line':
        return (
          <Line
            key={shape.id}
            {...commonProps}
            points={shape.points}
            stroke={shape.stroke}
            strokeWidth={3}
            lineCap="round"
            lineJoin="round"
          />
        );
      case 'text':
        return (
          <Text
            key={shape.id}
            {...commonProps}
            x={shape.x}
            y={shape.y}
            text={shape.text}
            fontSize={16}
            fill={shape.fill}
          />
        );
      default:
        return null;
    }
  };

  // Calculate scale to fit current canvas size
  const scaleX = width / drawingData.width;
  const scaleY = height / drawingData.height;

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
      <Stage
        ref={stageRef}
        width={width}
        height={height}
        scaleX={scaleX}
        scaleY={scaleY}
        onMouseDown={handleStageMouseDown}
        onMouseMove={handleStageMouseMove}
        onMouseUp={handleStageMouseUp}
        onTouchStart={handleStageMouseDown as any}
        onTouchMove={handleStageMouseMove as any}
        onTouchEnd={handleStageMouseUp as any}
        style={{ cursor: selectedTool === 'select' ? 'default' : 'crosshair' }}
      >
        <Layer>
          {/* Grid background */}
          {Array.from({ length: Math.ceil(drawingData.width / 50) }).map((_, i) => (
            <Line
              key={`grid-v-${i}`}
              points={[i * 50, 0, i * 50, drawingData.height]}
              stroke="#f1f5f9"
              strokeWidth={1 / Math.min(scaleX, scaleY)}
            />
          ))}
          {Array.from({ length: Math.ceil(drawingData.height / 50) }).map((_, i) => (
            <Line
              key={`grid-h-${i}`}
              points={[0, i * 50, drawingData.width, i * 50]}
              stroke="#f1f5f9"
              strokeWidth={1 / Math.min(scaleX, scaleY)}
            />
          ))}

          {/* Existing shapes */}
          {drawingData.shapes.map(renderShape)}

          {/* New shape being drawn */}
          {newShape && renderShape(newShape)}

          {/* Transformer for selected shape */}
          <Transformer
            ref={transformerRef}
            boundBoxFunc={(oldBox, newBox) => {
              // Limit minimum size
              if (newBox.width < 5 || newBox.height < 5) {
                return oldBox;
              }
              return newBox;
            }}
          />
        </Layer>
      </Stage>
    </div>
  );
};

export default FloorPlanCanvas;
